import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  useMediaQuery,
  Stack,
  InputAdornment,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  AttachFile as AttachFileIcon,
  CloudUpload as CloudUploadIcon,
  CloudDownload as CloudDownloadIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import SearchableSelect from '../../components/SearchableSelect';
import { useOperacionesUbicacionBloqueadas } from '../../contexts/UbicacionesUsuarioContext';
import { IUbicacion } from '../../interfaces/Ubicacion';
import conteoStockService, {
  extractUbicacionFromFormulario,
} from '../../services/conteoStockService';
import ubicacionService from '../../services/ubicacionService';
import productoService, {
  OPTIONS_PRODUCTO_OPERATIVO,
  PARAMS_PRODUCTO_OPERATIVO,
  type Producto,
} from '../../services/productoService';
import { useSucursalesUsuarioContext } from '../../contexts/SucursalesUsuarioContext';
import { useSucursalAltaFormulario } from '../../hooks/useSucursalAltaFormulario';
import { persistGlobalNroSuc } from '../../hooks/useSucursalesVentaFiltro';

interface ItemForm {
  codigoArticulo: string;
  descripcion: string;
  cantidadTeorica: number;
  cantidadContada: number;
  um: string;
  costo: number;
  notas: string;
  loadingTeorico?: boolean;
}

const emptyItem = (): ItemForm => ({
  codigoArticulo: '',
  descripcion: '',
  cantidadTeorica: 0,
  cantidadContada: 0,
  um: '',
  costo: 0,
  notas: '',
});

const umFromProducto = (producto: Producto | Record<string, unknown> | null | undefined): string => {
  if (!producto) return '';
  const p = producto as unknown as Record<string, unknown>;
  const umNav = p.unidadMedida;
  const nested =
    umNav && typeof umNav === 'object'
      ? String(
          (umNav as Record<string, unknown>).idUnidadMedida ??
            (umNav as Record<string, unknown>).descripcion ??
            '',
        ).trim()
      : typeof umNav === 'string'
        ? umNav.trim()
        : '';
  return String(p.idUnidadMedida ?? p.um ?? p.Um ?? nested ?? '').trim();
};

const formatCantidadConUm = (cantidad: number | string, um: string): string => {
  const base = String(cantidad);
  const unidad = String(um || '').trim();
  return unidad ? `${base} ${unidad}` : base;
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    bgcolor: '#fafafa',
    '&:hover': { bgcolor: '#f5f5f5' },
    '&.Mui-focused': { bgcolor: '#ffffff' },
  },
};

const ConteoStockForm: React.FC = () => {
  const navigate = useNavigate();
  const { nroFormulario } = useParams();
  const isDetalle = Boolean(nroFormulario);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const operacionesUbicacionBloqueadas = useOperacionesUbicacionBloqueadas();
  const { enqueueSnackbar } = useSnackbar();

  const [fecha, setFecha] = useState<string>(new Date().toISOString().split('T')[0]);
  const [nroSuc, setNroSuc] = useState<number>(0);
  const [descripcion, setDescripcion] = useState('');
  const [nroUbicacion, setNroUbicacion] = useState<number | null>(null);
  const [detUbicacion, setDetUbicacion] = useState<string | null>(null);
  const [usuario, setUsuario] = useState<string | null>(null);
  const [nroFormularioDetalle, setNroFormularioDetalle] = useState<number | null>(null);

  const [items, setItems] = useState<ItemForm[]>([emptyItem()]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ubicaciones, setUbicaciones] = useState<IUbicacion[]>([]);
  const { sucursales, loading: loadingSucursales } = useSucursalesUsuarioContext();

  const [openProductoSearch, setOpenProductoSearch] = useState(false);
  const [productoSearchIndex, setProductoSearchIndex] = useState<number | null>(null);
  const [productos, setProductos] = useState<any[]>([]);
  const [productoSearchTerm, setProductoSearchTerm] = useState('');
  const [productoSearchLoading, setProductoSearchLoading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [openImportar, setOpenImportar] = useState(false);
  const [importFileName, setImportFileName] = useState('');
  const [importPreviewRows, setImportPreviewRows] = useState<
    Array<{ codigo: string; cantidadNum: number; filaExcel: number }>
  >([]);
  const [importParseStats, setImportParseStats] = useState<{
    totalFilasExcel: number;
    erroresTotales: number;
    mensajesError: string[];
  }>({ totalFilasExcel: 0, erroresTotales: 0, mensajesError: [] });
  const [importResult, setImportResult] = useState<{
    total: number;
    exitosos: number;
    conErrores: number;
    mensajesError: string[];
  } | null>(null);
  const [isProcessingImport, setIsProcessingImport] = useState(false);

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nroUbicacionRef = useRef<number | null>(nroUbicacion);
  nroUbicacionRef.current = nroUbicacion;

  const sucursalOptions = useMemo(
    () =>
      sucursales.map((s) => ({
        value: s.nroSuc,
        label: `${s.nroSuc} - ${s.nombreSucursal?.trim() || `Sucursal ${s.nroSuc}`}`,
      })),
    [sucursales],
  );

  const ubicacionOptions = useMemo(() => {
    const options = ubicaciones.map((u) => ({
      value: Number(u.nroUbicacion),
      label: u.nombreUbicacion,
    }));
    if (nroUbicacion != null && nroUbicacion > 0) {
      const idx = options.findIndex((o) => o.value === Number(nroUbicacion));
      if (idx < 0) {
        options.push({
          value: Number(nroUbicacion),
          label: detUbicacion || `Ubicación ${nroUbicacion}`,
        });
      } else if (detUbicacion) {
        options[idx] = { ...options[idx], label: detUbicacion };
      }
    }
    return options;
  }, [ubicaciones, nroUbicacion, detUbicacion]);

  const loadUbicaciones = useCallback(async () => {
    try {
      const data = await ubicacionService.getUbicacionesParaUsuario();
      setUbicaciones(data);
    } catch (error) {
      console.error('Error al cargar ubicaciones:', error);
      enqueueSnackbar('Error al cargar las ubicaciones', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  const loadConteo = useCallback(
    async (numero: number) => {
      try {
        setLoading(true);
        const data = await conteoStockService.getConteo(numero);
        // GET /api/Conteos/{nro} responde FormularioDto (origen/destino), no nroUbicacion plano
        const cabecera = (data as any)?.conteo ?? (data as any)?.formulario ?? data;
        const itemsData = (data as any)?.items ?? cabecera?.items ?? [];

        let { nroUbicacion: nroUbicParsed, detUbicacion: detUbic } =
          extractUbicacionFromFormulario(data);

        if (nroUbicParsed && !detUbic) {
          try {
            const ubic = await ubicacionService.getUbicacion(nroUbicParsed);
            detUbic = ubic?.nombreUbicacion?.trim() || null;
          } catch {
            /* si falla, se muestra el nro */
          }
        }

        if (!nroUbicParsed) {
          console.warn(
            '[ConteoStock] GET sin ubicación usable. Claves cabecera:',
            cabecera && typeof cabecera === 'object' ? Object.keys(cabecera) : cabecera,
            'origen/destino:',
            {
              nroUbicacionOrigen: cabecera?.nroUbicacionOrigen ?? cabecera?.NroUbicacionOrigen,
              nroUbicacionDestino: cabecera?.nroUbicacionDestino ?? cabecera?.NroUbicacionDestino,
              nroUbicacion: cabecera?.nroUbicacion ?? cabecera?.NroUbicacion,
            },
          );
        }

        setNroFormularioDetalle(cabecera.nroFormulario ?? cabecera.NroFormulario);
        const fechaRaw =
          cabecera.fecha ?? cabecera.Fecha ?? cabecera.fechaEmision ?? cabecera.FechaEmision;
        const fechaVal = fechaRaw
          ? new Date(fechaRaw).toLocaleDateString('en-CA')
          : '';
        setFecha(fechaVal);
        setNroSuc(Number(cabecera.nroSuc ?? cabecera.NroSuc ?? 0) || 0);
        setDescripcion(cabecera.descripcion ?? cabecera.Descripcion ?? '');
        setNroUbicacion(nroUbicParsed);
        setDetUbicacion(detUbic);
        setUsuario(cabecera.usuario ?? cabecera.Usuario ?? null);
        const mappedItems: ItemForm[] = (itemsData as any[]).map((it) => ({
          codigoArticulo: it.codigoArticulo || it.CodigoArticulo || '',
          descripcion: it.descripcion || it.Descripcion || '',
          cantidadTeorica: Number(it.cantidadReal ?? it.CantidadReal ?? 0),
          cantidadContada: Number(it.cantidad ?? it.Cantidad ?? 0),
          um: umFromProducto(it),
          costo: Number(it.costo ?? it.Costo ?? 0),
          notas: it.notas || it.Notas || '',
        }));
        setItems(mappedItems.length ? mappedItems : [emptyItem()]);

        const codesWithoutUm = Array.from(
          new Set(
            mappedItems
              .filter((it) => it.codigoArticulo.trim() && !it.um)
              .map((it) => it.codigoArticulo.trim()),
          ),
        );
        if (codesWithoutUm.length) {
          const umResults = await Promise.allSettled(
            codesWithoutUm.map((code) =>
              productoService.getProducto(code, OPTIONS_PRODUCTO_OPERATIVO),
            ),
          );
          const umByCode = new Map<string, string>();
          codesWithoutUm.forEach((code, idx) => {
            const res = umResults[idx];
            if (res.status === 'fulfilled') {
              umByCode.set(code, umFromProducto(res.value));
            }
          });
          if (umByCode.size) {
            setItems((prev) =>
              prev.map((it) => {
                const um = umByCode.get(it.codigoArticulo.trim());
                return um && !it.um ? { ...it, um } : it;
              }),
            );
          }
        }
      } catch (error) {
        console.error('Error al cargar el conteo:', error);
        enqueueSnackbar('Error al cargar el conteo', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar],
  );

  useEffect(() => {
    loadUbicaciones();
    if (nroFormulario) {
      loadConteo(Number(nroFormulario));
    }
  }, [nroFormulario, loadConteo, loadUbicaciones]);

  const { onSucursalChange } = useSucursalAltaFormulario({
    nroSuc,
    setNroSuc,
    sucursales,
    loadingSucursales,
    enabled: !isDetalle,
  });

  useEffect(() => {
    if (Number.isFinite(nroSuc) && nroSuc > 0) {
      persistGlobalNroSuc(nroSuc);
    }
  }, [nroSuc]);

  // Si el detalle no trae nombre de ubicación, resolverlo desde el catálogo cargado.
  useEffect(() => {
    if (!isDetalle || !nroUbicacion || detUbicacion) return;
    const found = ubicaciones.find((u) => Number(u.nroUbicacion) === Number(nroUbicacion));
    if (found?.nombreUbicacion) {
      setDetUbicacion(found.nombreUbicacion);
    }
  }, [isDetalle, nroUbicacion, detUbicacion, ubicaciones]);

  const ubicacionDetalleLabel = useMemo(() => {
    if (!isDetalle) return '';
    const nombre =
      detUbicacion?.trim() ||
      ubicaciones.find((u) => Number(u.nroUbicacion) === Number(nroUbicacion))?.nombreUbicacion?.trim() ||
      '';
    if (nombre && nroUbicacion) return `${nombre} (${nroUbicacion})`;
    if (nombre) return nombre;
    if (nroUbicacion) return `Ubicación ${nroUbicacion}`;
    return '';
  }, [isDetalle, detUbicacion, ubicaciones, nroUbicacion]);

  const refreshTeorico = useCallback(
    async (index: number, codigo: string, ubicacion: number | null) => {
      if (!codigo.trim() || ubicacion == null || ubicacion <= 0) {
        setItems((prev) => {
          const next = [...prev];
          if (!next[index]) return prev;
          next[index] = { ...next[index], cantidadTeorica: 0, costo: 0, loadingTeorico: false };
          return next;
        });
        return;
      }

      setItems((prev) => {
        const next = [...prev];
        if (!next[index]) return prev;
        next[index] = { ...next[index], loadingTeorico: true };
        return next;
      });

      try {
        const stock = await conteoStockService.getStockUbicacion(codigo.trim(), ubicacion);
        const teorico = Number(stock.cantidadReal ?? stock.cantidad ?? 0);
        const costo = Number(stock.costo ?? 0);
        setItems((prev) => {
          const next = [...prev];
          if (!next[index] || next[index].codigoArticulo.trim() !== codigo.trim()) return prev;
          next[index] = {
            ...next[index],
            cantidadTeorica: teorico,
            costo,
            loadingTeorico: false,
          };
          return next;
        });
      } catch (error) {
        console.error('Error al obtener stock por ubicación:', error);
        setItems((prev) => {
          const next = [...prev];
          if (!next[index]) return prev;
          next[index] = { ...next[index], cantidadTeorica: 0, loadingTeorico: false };
          return next;
        });
      }
    },
    [],
  );

  const searchProduct = (codigo: string, index: number) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(async () => {
      if (!codigo.trim()) {
        setItems((prev) => {
          const next = [...prev];
          next[index] = { ...emptyItem() };
          return next;
        });
        return;
      }
      try {
        const producto = await productoService.getProducto(codigo.trim(), OPTIONS_PRODUCTO_OPERATIVO);
        setItems((prev) => {
          const next = [...prev];
          next[index] = {
            ...next[index],
            codigoArticulo: producto.codigoArticulo || codigo.trim(),
            descripcion: producto.descripcion || '',
            um: umFromProducto(producto),
            costo: Number(producto.costo ?? 0),
          };
          return next;
        });
        await refreshTeorico(index, producto.codigoArticulo || codigo.trim(), nroUbicacionRef.current);
      } catch (error) {
        console.error('Error al buscar el producto:', error);
        enqueueSnackbar(`El código de artículo "${codigo}" no existe`, { variant: 'error' });
        setItems((prev) => {
          const next = [...prev];
          next[index] = {
            ...next[index],
            descripcion: '',
            cantidadTeorica: 0,
            um: '',
            costo: 0,
          };
          return next;
        });
      }
    }, 500);
  };

  const handleItemCodigoChange = (index: number, value: string) => {
    setItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], codigoArticulo: value };
      return next;
    });
    searchProduct(value, index);
  };

  const handleItemContadoChange = (index: number, value: string) => {
    const num = value === '' ? 0 : Number(value);
    setItems((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        cantidadContada: Number.isFinite(num) ? num : 0,
      };
      return next;
    });
  };

  const addItem = () => setItems((prev) => [...prev, emptyItem()]);

  const removeItem = (index: number) => {
    setItems((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const normalizeCode = (code: unknown): string => {
    const raw = String(code ?? '').trim();
    if (raw === '') return '';
    if (/^\d+$/.test(raw)) return String(Number(raw));
    return raw.toUpperCase();
  };

  const validateCodesExist = async (rawCodes: string[]): Promise<string[]> => {
    const unique = Array.from(new Set(rawCodes.map((c) => normalizeCode(c)).filter((c) => c)));
    if (unique.length === 0) return [];
    const results = await Promise.allSettled(
      unique.map((c) => productoService.getProducto(c, OPTIONS_PRODUCTO_OPERATIVO)),
    );
    const missing: string[] = [];
    results.forEach((res, idx) => {
      if (res.status === 'rejected') missing.push(unique[idx]);
    });
    return missing;
  };

  const resetImportItemsDialog = () => {
    setImportFileName('');
    setImportPreviewRows([]);
    setImportParseStats({ totalFilasExcel: 0, erroresTotales: 0, mensajesError: [] });
    setImportResult(null);
    setIsProcessingImport(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDescargarPlantillaItems = () => {
    try {
      const headers = ['codigoArticulo', 'cantidad'];
      const data = [headers, new Array(headers.length).fill('')];
      const ws = XLSX.utils.aoa_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Items');
      XLSX.writeFile(wb, 'PlantillaItemsConteoStock.xlsx');
      enqueueSnackbar('Plantilla Excel descargada', { variant: 'success', autoHideDuration: 3000 });
    } catch {
      enqueueSnackbar('Error al crear la plantilla Excel', { variant: 'error', autoHideDuration: 4000 });
    }
  };

  const handleImportChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<boolean> => {
    const file = e.target.files?.[0];
    if (!file) return false;
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const rows: Record<string, unknown>[] = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

      type ParsedRow = { codigo: string; cantidadNum: number; filaExcel: number };
      const parsedRows: ParsedRow[] = [];
      const missingCodeCount = { count: 0 };
      const invalidCantidadCount = { count: 0 };

      const isProvided = (v: unknown) => v !== undefined && v !== null && String(v).trim() !== '';
      const toCantidad = (v: unknown): { valid: boolean; value: number } => {
        if (!isProvided(v)) return { valid: false, value: 0 };
        const n = Number(String(v).replace(',', '.'));
        if (isNaN(n) || !Number.isFinite(n) || n <= 0) return { valid: false, value: 0 };
        return { valid: true, value: n };
      };

      for (let idx = 0; idx < rows.length; idx++) {
        const r = rows[idx];
        const normalized: Record<string, unknown> = {};
        Object.keys(r).forEach((k) => {
          normalized[String(k).toLowerCase().trim()] = r[k];
        });
        const get = (key: string, alts: string[] = []) => {
          const keys = [key.toLowerCase(), ...alts.map((a) => a.toLowerCase())];
          for (const k of keys) {
            if (normalized[k] !== undefined) return normalized[k];
          }
          return undefined;
        };

        const codigo = normalizeCode(get('codigoArticulo', ['codigo', 'codigoproducto']));
        if (!codigo) {
          missingCodeCount.count++;
          continue;
        }

        const cantidad = toCantidad(get('cantidad'));
        if (!cantidad.valid) {
          invalidCantidadCount.count++;
          continue;
        }

        parsedRows.push({
          codigo,
          cantidadNum: cantidad.value,
          filaExcel: idx + 2,
        });
      }

      const mensajesError: string[] = [];
      if (missingCodeCount.count > 0) {
        mensajesError.push(`faltó codigoArticulo en ${missingCodeCount.count} fila(s)`);
      }
      if (invalidCantidadCount.count > 0) {
        mensajesError.push(
          `cantidad inválida en ${invalidCantidadCount.count} fila(s) (debe ser numérica y mayor a 0)`,
        );
      }

      setImportFileName(file.name);
      setImportPreviewRows(parsedRows);
      setImportParseStats({
        totalFilasExcel: rows.length,
        erroresTotales: missingCodeCount.count + invalidCantidadCount.count,
        mensajesError,
      });
      if (parsedRows.length === 0) {
        setImportResult({
          total: rows.length,
          exitosos: 0,
          conErrores: missingCodeCount.count + invalidCantidadCount.count,
          mensajesError: mensajesError.length ? mensajesError : ['El archivo no contiene filas válidas.'],
        });
      } else {
        setImportResult(null);
      }
      return true;
    } catch (error) {
      console.error('Error al importar Excel:', error);
      enqueueSnackbar('No se pudo leer el archivo Excel', { variant: 'error' });
      return false;
    } finally {
      e.target.value = '';
    }
  };

  const handleProcesarImportacionItems = async () => {
    if (isProcessingImport) return;
    setIsProcessingImport(true);
    try {
      if (!importPreviewRows.length) {
        setImportResult({
          total: importParseStats.totalFilasExcel,
          exitosos: 0,
          conErrores: importParseStats.erroresTotales,
          mensajesError: importParseStats.mensajesError.length
            ? importParseStats.mensajesError
            : ['El archivo no contiene filas válidas.'],
        });
        return;
      }

      if (nroUbicacion == null || nroUbicacion <= 0) {
        setImportResult({
          total: importParseStats.totalFilasExcel,
          exitosos: 0,
          conErrores: importParseStats.totalFilasExcel,
          mensajesError: ['Debe seleccionar una ubicación antes de importar ítems'],
        });
        return;
      }

      const countMap = new Map<string, number>();
      importPreviewRows.forEach((pr) => countMap.set(pr.codigo, (countMap.get(pr.codigo) || 0) + 1));

      const duplicatedCodesInFile = Array.from(countMap.entries())
        .filter(([, cnt]) => cnt > 1)
        .map(([code]) => code);

      const existingCodes = new Set<string>(
        items.map((i) => normalizeCode(i.codigoArticulo)).filter((c) => c !== ''),
      );

      const duplicatedCodesAgainstExisting = importPreviewRows
        .map((pr) => pr.codigo)
        .filter((code) => existingCodes.has(code));

      const mensajesError: string[] = [];

      if (duplicatedCodesInFile.length || duplicatedCodesAgainstExisting.length) {
        if (duplicatedCodesInFile.length) {
          const list = Array.from(new Set(duplicatedCodesInFile)).slice(0, 5).join(', ');
          mensajesError.push(
            `códigos duplicados en archivo: ${list}${duplicatedCodesInFile.length > 5 ? '…' : ''}`,
          );
        }
        if (duplicatedCodesAgainstExisting.length) {
          const list = Array.from(new Set(duplicatedCodesAgainstExisting)).slice(0, 5).join(', ');
          mensajesError.push(
            `códigos ya existentes: ${list}${duplicatedCodesAgainstExisting.length > 5 ? '…' : ''}`,
          );
        }

        setImportResult({
          total: importParseStats.totalFilasExcel,
          exitosos: 0,
          conErrores: importParseStats.totalFilasExcel,
          mensajesError,
        });
        return;
      }

      const missingCodes = await validateCodesExist(importPreviewRows.map((pr) => pr.codigo));
      if (missingCodes.length) {
        const sample = missingCodes.slice(0, 6).join(', ');
        setImportResult({
          total: importParseStats.totalFilasExcel,
          exitosos: 0,
          conErrores: importParseStats.totalFilasExcel,
          mensajesError: [
            `hay código(s) inexistente(s): ${sample}${missingCodes.length > 6 ? '…' : ''}`,
          ],
        });
        return;
      }

      const uniqueCodes = Array.from(new Set(importPreviewRows.map((pr) => pr.codigo)));
      const productsByCode = new Map<string, any>();
      const results = await Promise.allSettled(
        uniqueCodes.map((code) => productoService.getProducto(code, OPTIONS_PRODUCTO_OPERATIVO)),
      );
      uniqueCodes.forEach((code, idx) => {
        const res = results[idx];
        if (res.status === 'fulfilled') productsByCode.set(code, res.value);
      });

      const ubicacion = nroUbicacion;
      const stockByCode = new Map<string, { teorico: number; costo: number }>();
      const stockResults = await Promise.allSettled(
        uniqueCodes.map((code) => conteoStockService.getStockUbicacion(code, ubicacion)),
      );
      uniqueCodes.forEach((code, idx) => {
        const res = stockResults[idx];
        if (res.status === 'fulfilled') {
          const stock = res.value;
          stockByCode.set(code, {
            teorico: Number(stock.cantidadReal ?? stock.cantidad ?? 0),
            costo: Number(stock.costo ?? 0),
          });
        }
      });

      const isEmptyItem = (it: ItemForm) => {
        const codigo = String(it.codigoArticulo || '').trim();
        const desc = String(it.descripcion || '').trim();
        return (
          codigo === '' &&
          desc === '' &&
          Number(it.cantidadContada || 0) === 0 &&
          Number(it.cantidadTeorica || 0) === 0
        );
      };

      const cleanedExisting = items.filter((it) => !isEmptyItem(it));

      const imported: ItemForm[] = importPreviewRows.map((pr) => {
        const prod = productsByCode.get(pr.codigo);
        const stock = stockByCode.get(pr.codigo);
        return {
          codigoArticulo: pr.codigo,
          descripcion: prod?.descripcion ?? prod?.nombreLargo ?? '',
          cantidadTeorica: stock?.teorico ?? 0,
          cantidadContada: pr.cantidadNum,
          um: umFromProducto(prod),
          costo: stock?.costo ?? Number(prod?.costo ?? 0),
          notas: '',
        };
      });

      setItems([...cleanedExisting, ...imported]);

      enqueueSnackbar(`Importados ${imported.length} ítems.`, { variant: 'success' });
      setImportResult({
        total: importParseStats.totalFilasExcel,
        exitosos: imported.length,
        conErrores: importParseStats.erroresTotales,
        mensajesError: importParseStats.mensajesError,
      });
    } catch (err: any) {
      console.error('Error al procesar importación de items:', err);
      setImportResult({
        total: importParseStats.totalFilasExcel,
        exitosos: 0,
        conErrores: importParseStats.totalFilasExcel,
        mensajesError: [err?.message || 'Error al procesar la importación del archivo.'],
      });
    } finally {
      setIsProcessingImport(false);
    }
  };

  const handleOpenProductoSearch = (index: number) => {
    setProductoSearchIndex(index);
    setProductoSearchTerm('');
    setProductos([]);
    setOpenProductoSearch(true);
  };

  const handleProductoSearch = async () => {
    try {
      setProductoSearchLoading(true);
      const response = await productoService.getProductos({
        searchTerm: productoSearchTerm,
        pageNumber: 1,
        pageSize: 50,
        ...PARAMS_PRODUCTO_OPERATIVO,
      });
      setProductos(response.items);
    } catch {
      enqueueSnackbar('Error al buscar productos', { variant: 'error' });
    } finally {
      setProductoSearchLoading(false);
    }
  };

  const handleProductoSelect = async (producto: any) => {
    if (productoSearchIndex === null) return;
    const index = productoSearchIndex;
    try {
      const codigoArticulo = producto.codigoArticulo || producto.codigo;
      const productoCompleto = await productoService.getProducto(codigoArticulo, OPTIONS_PRODUCTO_OPERATIVO);
      setItems((prev) => {
        const next = [...prev];
        next[index] = {
          ...next[index],
          codigoArticulo,
          descripcion: productoCompleto.descripcion || producto.descripcion || '',
          um: umFromProducto(productoCompleto),
          costo: Number(productoCompleto.costo ?? producto.costo ?? 0),
        };
        return next;
      });
      await refreshTeorico(index, codigoArticulo, nroUbicacionRef.current);
    } catch (error) {
      console.error('Error al obtener el producto:', error);
      setItems((prev) => {
        const next = [...prev];
        next[index] = {
          ...next[index],
          codigoArticulo: producto.codigoArticulo || producto.codigo,
          descripcion: producto.descripcion || '',
          um: umFromProducto(producto),
        };
        return next;
      });
    }
    setOpenProductoSearch(false);
    setProductoSearchIndex(null);
    setProductoSearchTerm('');
    setProductos([]);
  };

  const handleUbicacionChange = (val: number | null) => {
    setNroUbicacion(val);
    setDetUbicacion(
      val != null ? ubicaciones.find((u) => u.nroUbicacion === val)?.nombreUbicacion || null : null,
    );
    if (!isDetalle) {
      // Refrescar teóricos de ítems con código al cambiar ubicación
      const codes = items
        .map((it, idx) => ({ idx, codigo: it.codigoArticulo.trim() }))
        .filter((x) => x.codigo);
      codes.forEach((x) => refreshTeorico(x.idx, x.codigo, val));
    }
  };

  const diferencia = (item: ItemForm) => item.cantidadContada - item.cantidadTeorica;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isDetalle || saving) return;

    if (!nroUbicacion || nroUbicacion <= 0) {
      enqueueSnackbar('La ubicación es obligatoria', { variant: 'warning' });
      return;
    }
    if (!nroSuc || nroSuc <= 0) {
      enqueueSnackbar('La sucursal es obligatoria', { variant: 'warning' });
      return;
    }
    if (!fecha) {
      enqueueSnackbar('La fecha es obligatoria', { variant: 'warning' });
      return;
    }

    const itemsValidos = items.filter((it) => it.codigoArticulo.trim());
    if (itemsValidos.length === 0) {
      enqueueSnackbar('Debe agregar al menos un ítem', { variant: 'warning' });
      return;
    }

    const negativos = itemsValidos.filter((it) => it.cantidadContada < 0);
    if (negativos.length > 0) {
      enqueueSnackbar('Las cantidades contadas deben ser mayores o iguales a 0', {
        variant: 'warning',
      });
      return;
    }

    const codigos = itemsValidos.map((it) => it.codigoArticulo.trim().toUpperCase());
    const duplicados = codigos.filter((c, i) => codigos.indexOf(c) !== i);
    if (duplicados.length > 0) {
      enqueueSnackbar(`Hay códigos duplicados: ${Array.from(new Set(duplicados)).join(', ')}`, {
        variant: 'warning',
      });
      return;
    }

    const sinDescripcion = itemsValidos.filter((it) => !it.descripcion.trim());
    if (sinDescripcion.length > 0) {
      enqueueSnackbar('Hay ítems con código inválido o sin producto resuelto', {
        variant: 'warning',
      });
      return;
    }

    try {
      setSaving(true);
      const fechaIso = new Date(`${fecha}T12:00:00`).toISOString();
      const response = await conteoStockService.createConteo({
        fecha: fechaIso,
        nroSuc,
        descripcion: descripcion || undefined,
        nroUbicacion,
        items: itemsValidos.map((it) => ({
          codigoArticulo: it.codigoArticulo.trim(),
          descripcion: it.descripcion,
          cantidadContada: Number(it.cantidadContada) || 0,
          notas: '',
        })),
      });

      const nroPos = response.nroAjustePositivo;
      const nroNeg = response.nroAjusteNegativo;
      const tienePos = nroPos != null && nroPos > 0;
      const tieneNeg = nroNeg != null && nroNeg > 0;

      if (response.mensaje) {
        enqueueSnackbar(response.mensaje, { variant: 'success' });
      } else if (!tienePos && !tieneNeg) {
        enqueueSnackbar('Conteo guardado. Sin diferencias (no se generaron ajustes).', {
          variant: 'success',
        });
      } else {
        const partes: string[] = ['Conteo guardado.'];
        if (tienePos) partes.push(`Ajuste positivo: ${nroPos}`);
        if (tieneNeg) partes.push(`Ajuste negativo: ${nroNeg}`);
        enqueueSnackbar(partes.join(' '), { variant: 'success' });
      }

      navigate('/inventario/conteo', {
        replace: false,
        state: { refresh: Date.now() },
      });
    } catch (error: any) {
      console.error('Error al guardar el conteo:', error);
      const msg =
        error?.response?.data?.mensaje ||
        error?.response?.data?.message ||
        error?.response?.data?.title ||
        'Error al guardar el conteo';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const renderItemFields = (item: ItemForm, index: number) => {
    const diff = diferencia(item);
    return (
      <>
        <TextField
          label="Código"
          value={item.codigoArticulo}
          onChange={(e) => handleItemCodigoChange(index, e.target.value)}
          size="small"
          fullWidth
          disabled={isDetalle}
          sx={fieldSx}
          InputProps={{
            readOnly: isDetalle,
            endAdornment: !isDetalle ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => handleOpenProductoSearch(index)} edge="end">
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
        <TextField
          label="Descripción"
          value={item.descripcion}
          size="small"
          fullWidth
          InputProps={{ readOnly: true }}
          sx={fieldSx}
        />
        <TextField
          label="Teórico"
          value={item.loadingTeorico ? '...' : formatCantidadConUm(item.cantidadTeorica, item.um)}
          size="small"
          fullWidth
          InputProps={{ readOnly: true }}
          sx={fieldSx}
        />
        <TextField
          label="Contado"
          type="number"
          value={item.cantidadContada}
          onChange={(e) => handleItemContadoChange(index, e.target.value)}
          size="small"
          fullWidth
          disabled={isDetalle}
          inputProps={{ min: 0, step: 'any' }}
          InputProps={{
            readOnly: isDetalle,
            endAdornment: item.um ? (
              <InputAdornment position="end">
                <Typography variant="body2" color="text.secondary">
                  {item.um}
                </Typography>
              </InputAdornment>
            ) : undefined,
          }}
          sx={fieldSx}
        />
        <TextField
          label="Diferencia"
          value={formatCantidadConUm(diff, item.um)}
          size="small"
          fullWidth
          InputProps={{ readOnly: true }}
          sx={{
            ...fieldSx,
            '& .MuiOutlinedInput-input': {
              color: diff > 0 ? 'success.main' : diff < 0 ? 'error.main' : 'text.primary',
              fontWeight: diff !== 0 ? 600 : 400,
            },
          }}
        />
      </>
    );
  };

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', p: { xs: 2, sm: 3 } }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', p: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/inventario/conteo')}
            sx={{ textTransform: 'none', alignSelf: { xs: 'flex-start', sm: 'center' } }}
          >
            Conteo de Stock
          </Button>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontSize: { xs: '1.5rem', sm: '2.125rem' },
              fontWeight: 600,
              color: '#1976d2',
            }}
          >
            {isDetalle
              ? `Conteo de Stock Nº ${nroFormularioDetalle ?? nroFormulario}`
              : 'Nuevo Conteo de Stock'}
          </Typography>
        </Stack>

        <form onSubmit={handleSubmit}>
          <Card sx={{ mb: 2, borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Fecha"
                    type="date"
                    value={fecha}
                    onChange={(e) => setFecha(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    disabled={isDetalle}
                    InputProps={{ readOnly: isDetalle }}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <SearchableSelect<number>
                    label="Nº Sucursal"
                    value={nroSuc > 0 ? nroSuc : null}
                    onChange={(val) => onSucursalChange(val ?? 0)}
                    options={sucursalOptions}
                    required
                    loading={loadingSucursales}
                    disabled={isDetalle}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  {isDetalle ? (
                    <TextField
                      fullWidth
                      label="Ubicación"
                      value={ubicacionDetalleLabel || 'Sin ubicación'}
                      disabled
                      InputLabelProps={{ shrink: true }}
                      sx={fieldSx}
                    />
                  ) : (
                    <SearchableSelect<number>
                      label="Ubicación"
                      value={nroUbicacion != null && nroUbicacion > 0 ? Number(nroUbicacion) : null}
                      onChange={handleUbicacionChange}
                      options={ubicacionOptions}
                      required
                      sx={fieldSx}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Descripción"
                    value={descripcion}
                    onChange={(e) => setDescripcion(e.target.value)}
                    disabled={isDetalle}
                    InputProps={{ readOnly: isDetalle }}
                    sx={fieldSx}
                  />
                </Grid>
                {isDetalle && usuario && (
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Usuario"
                      value={usuario}
                      disabled
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: true }}
                      sx={fieldSx}
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  mb: 2,
                  gap: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  Ítems
                </Typography>
                {!isDetalle && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 1,
                      width: { xs: '100%', sm: 'auto' },
                    }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<AttachFileIcon />}
                      onClick={() => {
                        resetImportItemsDialog();
                        setOpenImportar(true);
                      }}
                      fullWidth={isMobile}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      }}
                    >
                      {isMobile ? 'Importar' : 'Importar items'}
                    </Button>
                    <Button
                      startIcon={<AddIcon />}
                      onClick={addItem}
                      variant="contained"
                      color="primary"
                      fullWidth={isMobile}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                      }}
                    >
                      Agregar Ítem
                    </Button>
                  </Box>
                )}
              </Box>

              {isMobile ? (
                <Stack spacing={2}>
                  {items.map((item, index) => (
                    <Card key={index} variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight="bold" color="#1976d2">
                            Ítem #{index + 1}
                          </Typography>
                          {!isDetalle && (
                            <IconButton
                              color="error"
                              onClick={() => removeItem(index)}
                              disabled={items.length === 1}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                        <Stack spacing={2}>{renderItemFields(item, index)}</Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width="16%">Código</TableCell>
                        <TableCell width="28%">Descripción</TableCell>
                        <TableCell width="14%" align="right">
                          Teórico
                        </TableCell>
                        <TableCell width="16%" align="right">
                          Contado
                        </TableCell>
                        <TableCell width="14%" align="right">
                          Diferencia
                        </TableCell>
                        {!isDetalle && <TableCell width="6%" align="center" />}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((item, index) => {
                        const diff = diferencia(item);
                        return (
                          <TableRow key={index}>
                            <TableCell>
                              <TextField
                                value={item.codigoArticulo}
                                onChange={(e) => handleItemCodigoChange(index, e.target.value)}
                                size="small"
                                fullWidth
                                disabled={isDetalle}
                                sx={fieldSx}
                                InputProps={{
                                  readOnly: isDetalle,
                                  endAdornment: !isDetalle ? (
                                    <InputAdornment position="end">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleOpenProductoSearch(index)}
                                        edge="end"
                                      >
                                        <SearchIcon fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  ) : undefined,
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{item.descripcion || '-'}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              {item.loadingTeorico ? (
                                <CircularProgress size={16} />
                              ) : (
                                <Typography variant="body2" component="span">
                                  {formatCantidadConUm(item.cantidadTeorica, item.um)}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number"
                                value={item.cantidadContada}
                                onChange={(e) => handleItemContadoChange(index, e.target.value)}
                                size="small"
                                disabled={isDetalle}
                                inputProps={{ min: 0, step: 'any', style: { textAlign: 'right' } }}
                                InputProps={{
                                  readOnly: isDetalle,
                                  endAdornment: item.um ? (
                                    <InputAdornment position="end">
                                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                        {item.um}
                                      </Typography>
                                    </InputAdornment>
                                  ) : undefined,
                                }}
                                sx={{ ...fieldSx, width: item.um ? 140 : 100 }}
                              />
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                color: diff > 0 ? 'success.main' : diff < 0 ? 'error.main' : 'inherit',
                                fontWeight: diff !== 0 ? 600 : 400,
                              }}
                            >
                              {formatCantidadConUm(diff, item.um)}
                            </TableCell>
                            {!isDetalle && (
                              <TableCell align="center">
                                <IconButton
                                  color="error"
                                  onClick={() => removeItem(index)}
                                  disabled={items.length === 1}
                                  size="small"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="flex-end"
            sx={{ mt: 3 }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate('/inventario/conteo')}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              {isDetalle ? 'Volver' : 'Cancelar'}
            </Button>
            {!isDetalle && (
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving || operacionesUbicacionBloqueadas}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={22} color="inherit" /> : 'Guardar'}
              </Button>
            )}
          </Stack>
        </form>
      </Card>

      {/* Diálogo de importación de ítems */}
      <Dialog
        open={openImportar}
        onClose={() => setOpenImportar(false)}
        TransitionProps={{ onExited: resetImportItemsDialog }}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudUploadIcon sx={{ color: 'primary.main', fontSize: 30, mr: 1 }} />
            <Typography variant="h6" fontWeight={700}>
              Importar Items
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box>
            <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, mb: 3, p: 3, background: '#fafbfd' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CloudDownloadIcon color="primary" sx={{ fontSize: 30, mr: 1 }} />
                <Typography component="span" fontWeight={700} fontSize={18}>
                  1. Descargar Plantilla
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Descarga la plantilla Excel con el formato correcto para importar items. Solo incluye
                `codigoArticulo` y `cantidad`.
              </Typography>
              <Button
                variant="contained"
                color="info"
                fullWidth
                sx={{ fontWeight: 700, py: 1.2, mb: 1 }}
                startIcon={<CloudDownloadIcon />}
                onClick={handleDescargarPlantillaItems}
              >
                Descargar Plantilla
              </Button>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, p: 3, background: '#fafbfd' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CloudUploadIcon color="primary" sx={{ fontSize: 30, mr: 1 }} />
                <Typography component="span" fontWeight={700} fontSize={18}>
                  2. Importar Plantilla
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Selecciona tu archivo Excel completado. La plantilla solo pide `codigoArticulo` y
                `cantidad` (cantidad contada).
              </Typography>
              <label htmlFor="excel-upload-items-conteo">
                <input
                  id="excel-upload-items-conteo"
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    await handleImportChange(e);
                  }}
                />
                <Button
                  variant="contained"
                  fullWidth
                  color="primary"
                  startIcon={<CloudUploadIcon />}
                  component="span"
                  sx={{ fontWeight: 700, py: 1.2 }}
                >
                  Seleccionar Archivo
                </Button>
              </label>
              {importFileName && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CloudUploadIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                    <Typography variant="body2" fontWeight={600}>
                      {importFileName}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!importPreviewRows.length || isProcessingImport}
                    onClick={handleProcesarImportacionItems}
                    startIcon={
                      isProcessingImport ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <CloudUploadIcon />
                      )
                    }
                    sx={{ fontWeight: 700 }}
                  >
                    {isProcessingImport ? 'Procesando...' : 'Importar'}
                  </Button>
                </Box>
              )}
              {importResult && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                    Resultados de la Importación
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: importResult.conErrores > 0 ? '#ffebee' : '#ffedd5',
                      border: '1px solid',
                      borderColor: importResult.conErrores > 0 ? '#ffcdd2' : '#fed7aa',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      <strong>Total procesados:</strong> {importResult.total}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Exitosos:</strong> {importResult.exitosos}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Con errores:</strong> {importResult.conErrores}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Tasa de éxito:</strong>{' '}
                      {importResult.total > 0
                        ? `${Math.round((importResult.exitosos / importResult.total) * 100)}%`
                        : '0%'}
                    </Typography>
                  </Box>
                  {importResult.mensajesError.length > 0 && (
                    <Box
                      sx={{
                        borderRadius: 2,
                        border: '1px solid #ffe0b2',
                        bgcolor: '#fff3e0',
                        p: 2,
                        maxHeight: 200,
                        overflow: 'auto',
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                        Detalles de errores:
                      </Typography>
                      {importResult.mensajesError.map((m, idx) => (
                        <Typography
                          key={idx}
                          variant="body2"
                          sx={{ mb: 0.5, whiteSpace: 'pre-line' }}
                        >
                          {m}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenImportar(false)} color="inherit">
            {importResult ? 'Finalizar' : 'Cancelar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openProductoSearch}
        onClose={() => {
          setOpenProductoSearch(false);
          setProductoSearchIndex(null);
          setProductoSearchTerm('');
          setProductos([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Buscar Producto</DialogTitle>
        <DialogContent>
          <Box display="flex" gap={1} mb={2} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Buscar producto"
              value={productoSearchTerm}
              onChange={(e) => setProductoSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleProductoSearch();
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handleProductoSearch}
              disabled={productoSearchLoading}
            >
              Buscar
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productoSearchLoading && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                )}
                {!productoSearchLoading && productos.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      {productoSearchTerm
                        ? 'No se encontraron productos'
                        : 'Ingrese un término de búsqueda'}
                    </TableCell>
                  </TableRow>
                )}
                {productos.map((producto) => (
                  <TableRow key={producto.codigoArticulo || producto.codigo}>
                    <TableCell>{producto.codigoArticulo || producto.codigo}</TableCell>
                    <TableCell>{producto.descripcion || '-'}</TableCell>
                    <TableCell align="center">
                      <Button variant="outlined" onClick={() => handleProductoSelect(producto)}>
                        Seleccionar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenProductoSearch(false);
              setProductoSearchIndex(null);
              setProductoSearchTerm('');
              setProductos([]);
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConteoStockForm;
